/**
 * Service-worker registration (T71, plan D46 / repo D167). Web build only.
 *
 * TWO guards, and both are needed:
 * - `import.meta.env.MODE === "web"` is replaced at build time, so in
 *   `--mode native` the whole branch below is dead code and is dropped —
 *   `virtual:pwa-register`, which only exists when `vite.config.ts` adds the
 *   PWA plugin, is never resolved, and the native bundle names no service
 *   worker at all (T71's verify greps `dist/` for one).
 * - `isNative()` covers a web build loaded inside the Android shell anyway:
 *   there a service worker would fight the Capgo updater (T70) over the
 *   `https://localhost` origin, keep serving the old bundle after a swap, and
 *   defeat its rollback.
 *
 * `registerType: "prompt"`: nothing here reloads the page on its own. The new
 * version waits until the person taps "Atualizar", which is never offered
 * during a workout (`update-prompt.tsx`).
 */
import { isNative } from "../../native";
import { recheckOnReturn, setWebUpdater } from "./web-update";

/** Never throws and never delays the first render. */
export function registerPwa(): void {
  if (import.meta.env.MODE === "web" && !isNative()) {
    registerWeb().catch((error: unknown) => {
      console.error("[pwa] registration failed", error);
    });
  }
}

async function registerWeb(): Promise<void> {
  const { registerSW } = await import("virtual:pwa-register");
  const updateSW = registerSW({
    onNeedRefresh() {
      promptUpdate(() => updateSW(true)).catch((error: unknown) => {
        console.error("[pwa] update prompt failed", error);
      });
    },
    // Another tab's "Atualizar" hands every tab to the new worker; the
    // library's default is an immediate reload, which could land mid-set.
    onNeedReload() {
      reloadLater().catch(() => window.location.reload());
    },
    onOfflineReady() {
      console.info("[pwa] app shell cached; the app now opens offline");
    },
    // A tab left open for days hears of a release when it comes back to the
    // foreground, not only at the next navigation (`web-update.ts`).
    onRegisteredSW(_url, registration) {
      if (registration !== undefined) {
        recheckOnReturn(registration);
      }
    },
    onRegisterError(error: unknown) {
      console.error("[pwa] service worker registration failed", error);
    },
  });
  // "Procurar atualizações" applies a waiting version the same way.
  setWebUpdater(updateSW);
}

// The prompt is loaded lazily, and through `await import()` with the names
// destructured at the declaration: `pnpm deadcode` (fallow) follows that
// shape to the two exports, and did not follow the `.then(({ … }) => …)`
// callback form this replaced, reporting both as unused.
async function promptUpdate(apply: () => Promise<void>): Promise<void> {
  const { promptForUpdate } = await import("./update-prompt");
  promptForUpdate(apply);
}

async function reloadLater(): Promise<void> {
  const { reloadWhenIdle } = await import("./update-prompt");
  reloadWhenIdle();
}
